import * as React from 'react';
import { BlockUserPostModel, ReadyToOrderPostModel, TestUserPostModel, UserResultModel, UsersClient } from "../../../swagger-clients/s365-admin-panel-clients.service";
import { toast } from "react-toastify";
import LoadingService from "../../../components/loading-indicator/loading-indicator.service";
import { getDashboardUsersClient, getPanelUsersClient, getTestUsersClient, getUserTagsClient } from '../../../services/api-clients.service';
import { RegistrationConfirmationPostModel } from '../../../swagger-clients/dashboard-clients.service';
import { ResetPasswordModal } from '../reset-password-modal.component';
import ToggleButton from 'react-bootstrap/ToggleButton';
import { EmailWithLinkToDomain } from '../../../components/email-with-link-to-domain/email-with-link-to-domain.component';
import targetProcessLogo from "../../../assets/images/target-process-logo.png";
import { TargetProcessLink } from '../../../components/target-process-link/target-process-link.component';
import CreateMotherTicketModal from '../create-mother-ticket-modal.component';
import { CreateMotherTicketButton } from '../create-mother-ticket-button.component';
import { DateTimeDisplayUTC } from '../../../components/date-time-display/date-time-display-utc.component';
import { ShowErrorFromResponse } from '../../../utilities/response-processor';
import SystemUserTagCodes from '../users.shared';
import { DateTimeDisplay } from '../../../components/date-time-display/date-time-display.component';
import PermissionGuard from '../../../auth/PermissionGuard';
import { UserRole } from '../../../auth/user-roles';

interface IUserGeneralDataProps {
    userId: string;
    onMotherTicketChanged: (motherTicketId: number, userId: string) => void;
}

const UserGeneralData: React.FunctionComponent<IUserGeneralDataProps> = (props) => {

    const [user, setUser] = React.useState<UserResultModel>();
    const [showResetPasswordModal, setShowResetPasswordModal] = React.useState<boolean>(false);
    const [requestInProgress, setRequestInProgress] = React.useState<boolean>(false);
    const [showCreateMotherTicketModal, setShowCreateMotherTicketModal] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (props.userId && props.userId.length > 0) {
            getUserDetails();
        }

    }, [props.userId]);

    const getUserDetails = async () => {
        try {
            LoadingService.showLoading();

            const client = new UsersClient(process.env.REACT_APP_BASE_URL);
            const userDetails = await client.getUserDetails(props.userId);
            setUser(userDetails);


        } catch (error) {
            console.log("An error occurred while getting user details.", error);
            toast.error("An error occurred while getting user details.");
        } finally {
            LoadingService.hideLoading();
        }
    }

    const resetPasswordClick = async () => {
        try {
            setRequestInProgress(true);
            var client = getDashboardUsersClient();


        } catch (error) {
            console.log("An error occurred while sending confirmation mail.", error);
        } finally {
            setRequestInProgress(false);
        }
    }

    const onBlockUserClick = async (user: UserResultModel) => {
        try {
            setRequestInProgress(true);
            const client = getPanelUsersClient();
            const model = {
                isAccountEnabled: user.isBlocked,
                userId: user.id
            } as BlockUserPostModel;
            await client.blockUnblockUser(model);
            getUserDetails();
            toast.success(`User successfully ${model.isAccountEnabled ? 'unblocked' : 'blocked'}.`);

        } catch (error) {
            console.log("An error occurred while blocking / unblocking user.", error);
            toast.error("An error occurred while blocking / unblocking user.");
        } finally {
            setRequestInProgress(false);
        }
    }

    const sendConfirmationMail = async () => {
        try {
            setRequestInProgress(true);
            var client = getDashboardUsersClient();
            var accountConfirmationUrl = process.env.REACT_APP_ACCOUNT_CONFIRMATION_PAGE;
            var email = user?.email;
            if (accountConfirmationUrl && email) {
                await client.resendEmailConfirmation({
                    email: email,
                    accountConfirmationPage: accountConfirmationUrl
                } as RegistrationConfirmationPostModel);
                toast.success("Registration confirmation request sent.");

            } else {
                console.log("Email, accountConfirmationUrl", email, accountConfirmationUrl);
                toast.error("Email or account confirmation url missing.");
            }

        } catch (error) {
            console.log("An error occurred while sending confirmation mail.", error);
        } finally {
            setRequestInProgress(false);
        }
    }

    const onAddTestUserClick = async () => {
        try {
            setRequestInProgress(true);
            const client = getTestUsersClient();
            await client.addTestUser(new TestUserPostModel({ userId: props.userId }));
            toast.success("Test user successfully added.");
            getUserDetails();

        } catch (error) {
            ShowErrorFromResponse(error, "An error occurred while adding test user.");
        }
        finally {
            setRequestInProgress(false);
        }
    }

    const onRemoveTestUserClick = async () => {
        try {
            setRequestInProgress(true);
            const client = getTestUsersClient();
            await client.deleteTestUser(props.userId);
            toast.success("Test user successfully removed.");
            getUserDetails();

        } catch (error) {
            ShowErrorFromResponse(error, "An error occurred while removing test user.");
        }
        finally {
            setRequestInProgress(false);
        }
    }

    const onChangeReadyToOrderClick = async (userId: string, canOrder: boolean) => {
        try {
            const client = getUserTagsClient();
            const model = new ReadyToOrderPostModel({
                canOrder: canOrder,
                userId: userId
            });
            await client.addOrUpdateReadyToOrderTag(model);
            toast.success("Successfully changed ready to order status.");
            await getUserDetails();
        } catch (error) {
            ShowErrorFromResponse(error, "An error occurred while changing ready to order status.");
        }

    }

    const readyToOrderTag = React.useMemo(() => {
        const tag = user?.tags?.find(x => x.code == SystemUserTagCodes.READY_TO_ORDER);
        return tag;
    }, [user]);

    const otherUserTags = React.useMemo(() => {
        const tag = user?.tags?.filter(x => x.code !== SystemUserTagCodes.READY_TO_ORDER && x.code !== SystemUserTagCodes.TEST_USER);
        return tag;
    }, [user]);

    return <dl className="row mb-0 mt-4 ml-2 align-items-center">
        <dt className="col-sm-3">Display name:</dt>
        <dd className="col-sm-9">{user ? user.displayName : ""}</dd>

        <dt className="col-sm-3">User principal name:</dt>
        <dd className="col-sm-9">{user ? user.userPrincipalName : ""}</dd>

        <dt className="col-sm-3">User Id:</dt>
        <dd className="col-sm-9">{user ? user.id : ""}</dd>

        <dt className="col-sm-3">Email:</dt>
        <dd className="col-sm-9" style={{ display: "flex", gap: "20px" }}>
            <EmailWithLinkToDomain email={user?.email} />

            {!!user?.motherTicket && <TargetProcessLink targetProcesMotherTicketId={user.motherTicket.id} />}
            <PermissionGuard allowedRoles={[UserRole.Admin]}>
                {!user?.motherTicket &&
                    <CreateMotherTicketButton
                        onClick={() => { setShowCreateMotherTicketModal(true); }}
                    />}
            </PermissionGuard>
        </dd>

        {!!user?.businessEmail && <>
            <dt className="col-sm-3">Business email:</dt>
            <dd className="col-sm-9" style={{ display: "flex", gap: "20px" }}>
                <EmailWithLinkToDomain email={user?.businessEmail} /> {!!user && !user.isBusinesEmailConfirmed && <span>(Not confirmed)</span>}
            </dd>
        </>}
        <dt className="col-sm-3">Business phone:</dt>
        <dd className="col-sm-9">{user?.businessPhone ?? "-"}</dd>

        <PermissionGuard allowedRoles={[UserRole.Admin]}>
            {!!user &&
                (!user?.tags || user?.tags?.length == 0 || user.tags.find(x => x.code == SystemUserTagCodes.TEST_USER) === undefined) && [
                    <dt className="col-sm-3">Test user:</dt>,
                    <dd className="col-sm-9">
                        <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                            <button
                                type="button"
                                disabled={requestInProgress}
                                onClick={() => { onAddTestUserClick() }}
                                className="btn btn-sm btn-primary">Add test user</button>
                        </div>

                    </dd>]}
        </PermissionGuard>

        {!!user &&
            (!user?.tags || user?.tags?.length == 0 || user.tags.find(x => x.code == SystemUserTagCodes.TEST_USER) !== undefined) && [
                <dt className="col-sm-3">Test user:</dt>,
                <dd className="col-sm-9">
                    <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                        <span className='mr-2'>YES</span>
                        <PermissionGuard allowedRoles={[UserRole.Admin]}>
                            <button
                                type="button"
                                disabled={requestInProgress}
                                onClick={() => { onRemoveTestUserClick() }}
                                className="btn btn-sm btn-primary">Remove</button>
                        </PermissionGuard>

                    </div>

                </dd>]}


        {!!user &&
            (!user?.tags || user?.tags?.length == 0 ||
                (user.tags.find(x => x.code == SystemUserTagCodes.CONFIRMED_EMAIL) === undefined)
                && user.tags.find(x => x.code == SystemUserTagCodes.CONFIRMED_BUSINESS_EMAIL) === undefined) && [
                <dt className="col-sm-3">Email confirmed at:</dt>,
                <dd className="col-sm-9">
                    <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                        <span className="mr-3">{user?.createdAt < new Date("2021-07-01") ? <small><em>User was registered before functionality was available.</em></small> : <span>Not confirmed</span>}</span>
                        <PermissionGuard allowedRoles={[UserRole.Admin]}>
                            <button
                                type="button"
                                disabled={requestInProgress}
                                onClick={sendConfirmationMail}
                                className="btn btn-sm btn-primary">Resend</button>
                        </PermissionGuard>

                    </div>
                </dd>
            ]}
        <dt className="col-sm-3">Ready to order:</dt>
        <dd className="col-sm-9">
            {!!readyToOrderTag && <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                <span className='mr-2'><b>YES</b></span>
                <div className="mr-3 d-flex flex-column">
                    <span>Created by: {user?.readyToOrderCratedBy}</span>
                    {!!readyToOrderTag.eventAtUtc && <DateTimeDisplay date={readyToOrderTag.eventAtUtc} />}
                </div>
                <PermissionGuard allowedRoles={[UserRole.Admin]}>
                    <button
                        type="button"
                        disabled={requestInProgress}
                        onClick={() => onChangeReadyToOrderClick(user.id, false)}
                        className="btn btn-sm btn-primary">Not Ready</button>
                </PermissionGuard>

            </div>}
            {!readyToOrderTag && <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                <span className='mr-2'><b>NO</b></span>
                <PermissionGuard allowedRoles={[UserRole.Admin]}>
                    <button
                        type="button"
                        disabled={requestInProgress}
                        onClick={() => onChangeReadyToOrderClick(user.id, true)}
                        className="btn btn-sm btn-primary">Ready</button>
                </PermissionGuard>

            </div>}


        </dd>


        {otherUserTags?.map(userTag => {
            return [
                <dt className="col-sm-3">{userTag.name}:</dt>,
                <dd className="col-sm-9">{!!userTag?.eventAtUtc ? <DateTimeDisplayUTC date={userTag.eventAtUtc} /> : "YES"}</dd>
            ];
        })}
        <PermissionGuard allowedRoles={[UserRole.Admin]}>
            {user && <>
                <dt className="col-sm-3">Reset password:</dt>
                <dd className="col-sm-9">
                    <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                        <button
                            type="button"
                            disabled={requestInProgress}
                            onClick={() => setShowResetPasswordModal(true)}
                            className="btn btn-sm btn-primary">Reset</button>
                    </div>

                </dd>
            </>}
        </PermissionGuard>


        <dt className="col-sm-3">Blocked sign in:</dt>
        <dd className="col-sm-9">
            <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                <span className="mr-3">  {user ? user.isBlocked ? "Yes" : "No" : ""}</span>
                <PermissionGuard allowedRoles={[UserRole.Admin]}>
                    <button
                        type="button"
                        disabled={requestInProgress}
                        onClick={() => { onBlockUserClick(user!) }}
                        className="btn btn-sm btn-primary">{user ? user.isBlocked ? "Unblock" : "Block" : ""}</button>
                </PermissionGuard>
            </div>

        </dd>

        {/* <dt className="col-sm-3">Test user:</dt>
        <dd className="col-sm-9">
            <div style={{ display: "flex", flexDirection: 'row', alignItems: "center" }}>
                <span className="mr-3">  {user ? user.isTestUser ? "Yes" : "No" : ""}</span>
                {!!user && !user.isTestUser && <button
                    type="button"
                    disabled={requestInProgress}
                    onClick={() => { onAddTestUserClick() }}
                    className="btn btn-sm btn-primary">Add test user</button>}
            </div>

        </dd> */}

        {showResetPasswordModal && <ResetPasswordModal show={true} onHide={() => setShowResetPasswordModal(false)} userDisplayName={user?.displayName!} userId={user?.id!} />}
        {showCreateMotherTicketModal && user &&
            <CreateMotherTicketModal
                show={true}
                user={user}
                onSuccess={(motherTicketId: number) => {
                    //  setUser({ ...user, targetProcesMotherTicketIds: [...(user.targetProcesMotherTicketIds ?? []), motherTicketId] } as UserResultModel);
                    setShowCreateMotherTicketModal(false);
                    props.onMotherTicketChanged(motherTicketId, user.id!);
                }}
                onHide={() => { setShowCreateMotherTicketModal(false); }}

            />}

    </dl>;
}

export default UserGeneralData;